import React, { useEffect, useState, useRef } from "react";
import { Col, Form, Row } from "react-bootstrap";
import axios from "../../services/api/HttpService";
import { useTranslation } from "react-i18next";

import FeaturedArticules from "../featuredArticules";
import Notification from "../notifications/notification";
import Programs from "../programs/programs";
import Carrousel from "../carrousel/carrousel";
import HomeSchedule from "../homeSchedule";
import FeaturedRecipes from "../featuredRecipes";
import DivLinks from "../divLinks";
import { setLocation } from "../../services/reducers/homeReducer";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Mixpanel } from "../../utils/mixpanelIntegration";
import UserService from "../../services/api/UserService";
import { getTokenMedia } from "../../services/actions/home.action";
import NewsLetter from "./newsletter";
import FlipBookModal from "../flipbook/FlipBookModal";
import ModalConfirmation from "../modalconfirmation/ModalConfirmation";



const filterLocations = (locations, value) =>
  locations?.filter(
    (location) =>
      !value ||
      location.title?.toLowerCase().includes(value.toLowerCase()) ||
      location.short_name?.toLowerCase().includes(value.toLowerCase()) ||
      location.type?.toLowerCase().includes(value.toLowerCase())
  ) || [];


const Retiree = () => {


  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [retireeNavigationSource, setRetireeNavigationSourceLocal] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;
  const SHOW_RETIREE_FLIP_BOOK = process.env.SHOW_RETIREE_FLIP_BOOK;

  const [retireesList, setRetireesList] = useState([]);
  const [selectedRetiree, setSelectedRetiree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRetireeNotAvailable, setIsRetireeNotAvailable] = useState(false);
  const [unavailableLocationName, setUnavailableLocationName] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const filteredLocations = filterLocations(retireesList, searchValue);

  const locationSelected = async (retireeId, locationName) => {
    Mixpanel.track("Retiree.LocationSelected", {
      isLogged: UserService.isLoggedIn(),
      isRetireePortal: true,
      retireePortalId: retireeId,
      locationName: locationName,
    });
    localStorage.setItem('selectedRetireeId', retireeId);
    setSelectedRetiree(retireeId);
    dispatch(setLocation({ location: { data: { title: locationName } } }))
  };



  useEffect(() => {
    console.log('[Retiree] useEffect running');
    console.log('[Retiree] window.location.search:', window.location.search);
    console.log('[Retiree] window.location.href:', window.location.href);

    // Check URL parameters first for cross-domain navigation support
    const urlParams = new URLSearchParams(window.location.search);
    const retireeIdFromUrl = urlParams.get('retireeId'); // Only present if the navigation was from member portal
    const navigationSourceFromUrl = urlParams.get('source'); // Only present if the navigation was from member portal

    console.log('[Retiree] retireeIdFromUrl:', retireeIdFromUrl);
    console.log('[Retiree] navigationSourceFromUrl:', navigationSourceFromUrl);
    console.log('[Retiree] localStorage.selectedRetireeId:', localStorage.getItem('selectedRetireeId'));

    // Prioritize URL params over storage (for cross-domain scenarios)
    const navigationSource = navigationSourceFromUrl || localStorage.getItem('retireeNavigationSource');
    setRetireeNavigationSourceLocal(navigationSource);

    // Store navigation source in localStorage for same-origin fallback
    if (navigationSourceFromUrl) {
      console.log('[Retiree] Storing navigationSource in localStorage:', navigationSourceFromUrl);
      localStorage.setItem('retireeNavigationSource', navigationSourceFromUrl);
    }

    // Get retireeId from URL params first, then localStorage
    const savedRetireeId = retireeIdFromUrl || localStorage.getItem('selectedRetireeId');
    console.log('[Retiree] Final savedRetireeId:', savedRetireeId);

    if (savedRetireeId) {
      // Store in localStorage for same-origin fallback
      if (retireeIdFromUrl) {
        console.log('[Retiree] Storing retireeId in localStorage:', retireeIdFromUrl);
        localStorage.setItem('selectedRetireeId', retireeIdFromUrl);
      }
      setSelectedRetiree(savedRetireeId);
      fetchRetireeData(savedRetireeId);

      // Clean up URL params after storing them in localStorage. Only if user navigated from the member portal
      if (retireeIdFromUrl || navigationSourceFromUrl) {
        console.log('[Retiree] Cleaning up URL parameters');
        // Use replaceState to remove params without triggering a page reload
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else {
      console.log('[Retiree] No savedRetireeId, fetching retirees list');
      fetchRetireesList();
    }
  }, []);

  const fetchRetireeData = async (retireeId) => {
    setLoading(true);
    setError(null);
    setIsRetireeNotAvailable(false);
    try {
      const response = await axios.get(`${API_URL}api/retiree/${retireeId}`);
      const data = {
        data: { title: response?.data?.data?.title }
      }
      dispatch(setLocation({ location: data }))
    } catch (err) {
      if (err.response && err.response.status === 404) {
        // Location doesn't have retiree option enabled
        const locationName = err.response?.data?.error?.name || null;
        setUnavailableLocationName(locationName);
        setIsRetireeNotAvailable(true);
        localStorage.removeItem('selectedRetireeId');
        setSelectedRetiree(null);
      } else {
        setError("Failed to fetch retirees list");
        console.error("Error fetching retirees:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRetireesList = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}api/retiree`);
      setRetireesList(response?.data?.data);
    } catch (err) {
      setError("Failed to fetch retirees list");
      console.error("Error fetching retirees:", err);
    } finally {
      setLoading(false);
    }
  };

  const RetireeSelector = () => {
    const { t } = useTranslation();
    const searchInputRef = useRef(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(null);

    useEffect(() => {
      const savedRetireeId = localStorage.getItem('selectedRetireeId');
      if (!savedRetireeId) {
        Mixpanel.track("Retiree.SelectorPageVisited", {
          isLogged: UserService.isLoggedIn(),
          isRetireePortal: true,
          retireePortalId: null,
        });
      }
    }, []);

    const handleLocationClick = (retireeId, locationName) => {
      setPendingSelection({ retireeId, locationName });
      setShowConfirmModal(true);
    };

    const handleConfirmSelection = () => {
      if (pendingSelection) {
        locationSelected(pendingSelection.retireeId, pendingSelection.locationName);
        setShowConfirmModal(false);
        setPendingSelection(null);
      }
    };

    const handleCancelSelection = () => {
      setPendingSelection(null);
    };

    return (

      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }}>
        <h1><strong>{t("retirees.retirementWelcome")}</strong></h1>
        <h3>{t("retirees.retirementLocationNote")}</h3>
        <div style={{ marginTop: "2.5rem", marginBottom: "1.5rem", width: '100%', display: 'flex', flexDirection: 'row' }}>
          <h3 style={{ marginTop: '0.5rem' }}>{t("retirees.retirementLocationQuestion")}</h3>
          <div
            style={{
              marginLeft: "1.5rem",
              width: "70%",
              overflowY: "scroll",
              height: "20rem",
              maxHeight: "40%",
              border: "1px solid #ccc",
              borderRadius: "10px",
              padding: "10px",
              backgroundColor: "#fff",
            }}
          >
            <Form.Control
              ref={searchInputRef}
              className="mb-2 search-input-role"
              placeholder={t("retirees.retirementSearchPlaceHolder")}
              onChange={(e) => {
                const value = e.target.value;
                setSearchValue(value);
                Mixpanel.track("Retiree.Search", {
                  isLogged: UserService.isLoggedIn(),
                  isRetireePortal: true,
                  searchTerm: value,
                  resultsCount: filterLocations(retireesList, value).length
                });
              }}
              value={searchValue}
              autoFocus
            />
            <ul className="list-unstyled" style={{ margin: 0, padding: 0 }}>
              {filteredLocations.length === 0 ? (
                <li style={{ textAlign: "center", padding: "5px", color: "gray" }}>
                  No results found
                </li>
              ) : (
                filteredLocations.map((location) => (
                  <li
                    key={location.uniqueNonGuessableId}
                    onClick={() => handleLocationClick(location.retireeId, location.title)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "5px 10px",
                      cursor: "pointer",
                    }}
                  >
                    <span style={{ fontSize: "20px", fontWeight: "600", color: "black" }}> {location.title} </span>
                    <span style={{ fontSize: "16px", color: "grey" }}>{location.type}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
        <img
          alt="Group of older adults smiling and laughing while taking a break during a hike, leaning on a wooden railing in a forest"
          style={{ width: "80%", borderRadius: "2rem", marginTop: "1rem" }}
          src="https://miiaprod.blob.core.windows.net/miia-prod-public/Retiree%20Review_%20older%20group%20of%20people%20taking%20a%20break%20during%20a%20hike_shutterstock_2243057993.jpg" />

        <ModalConfirmation
          show={showConfirmModal}
          handleShow={setShowConfirmModal}
          title={t("retirees.confirmSelectionTitle")}
          question={pendingSelection && t("retirees.confirmSelectionMessage", { locationName: pendingSelection.locationName })}
          mainAction={handleConfirmSelection}
          secondaryAction={handleCancelSelection}
          mainButton={t("retirees.confirmButton")}
          secondaryButton={t("retirees.cancelButton")}
        />
      </div>
    );

  };

  const RetireeNotAvailable = ({ locationName }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleBackToSelector = () => {
      localStorage.removeItem('selectedRetireeId');
      window.location.reload(); // Reload to reset state
    };

    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        padding: "2rem",
        textAlign: "center"
      }}>
        <div style={{
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "10px",
          padding: "3rem",
          maxWidth: "600px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}>
          <h2 style={{ color: "#6c757d", marginBottom: "1.5rem" }}>
            {t("retirees.notAvailableTitle")}
          </h2>
          <p style={{ color: "#6c757d", fontSize: "1.1rem", marginBottom: "2rem" }}>
            {locationName ?
              t("retirees.notAvailableMessage", { locationName }) :
              t("retirees.notAvailableMessageGeneric")
            }
          </p>
          <button
            onClick={handleBackToSelector}
            style={{
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              cursor: "pointer",
              transition: "background-color 0.3s"
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = "#0056b3"}
            onMouseOut={(e) => e.target.style.backgroundColor = "#007bff"}
          >
            {t("retirees.backToSelection")}
          </button>
        </div>
      </div>
    );
  };

  const RetireeContent = ({ selectedRetiree }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const hasTrackedVisit = useRef(false);
    const [showFlipBook, setShowFlipBook] = useState(false);

    console.log("RetireeContent component rendered, selectedRetiree:", selectedRetiree);

    useEffect(() => {
      console.log("RetireeContent useEffect called, hasTrackedVisit:", hasTrackedVisit.current);
      if (!hasTrackedVisit.current) {
        console.log("Tracking Retiree.ContentPageVisited event");
        Mixpanel.track("Retiree.ContentPageVisited", {
          isLogged: UserService.isLoggedIn(),
          isRetireePortal: true,
          retireePortalId: selectedRetiree,
        });
        hasTrackedVisit.current = true;
      }

      // Renew access token
      getTokenMedia(dispatch);
    }, [dispatch, selectedRetiree]);

    return (
      <div>
        <Col md={12} xl={12} className="homePage-content">
          <Row style={{ padding: "0rem 1rem", margin: "0px" }}>
            <Notification retireeId={selectedRetiree} />
          </Row>

          <Row style={{ padding: "0rem 1rem", margin: "0px" }}>
            <Col md={12} xl={12} className="homePage-programs">
              <Programs retireeId={selectedRetiree} />
            </Col>
          </Row>

          <Row style={{ padding: "0px", margin: "0px" }}>
            <Col md={12} xl={12} className="homePage-risk-managment">
              <Carrousel retireeId={selectedRetiree} />
            </Col>
          </Row>

          <Row style={{ padding: "0px", margin: "15px 0px" }}>
            <Col md={12} xl={6} className="homePage-wellness-classes">
              <HomeSchedule retireeId={selectedRetiree} />
            </Col>
            <Col md={12} xl={6} className="homePage-twitter">
              <DivLinks retireeId={selectedRetiree} />
            </Col>
          </Row>

          <Row style={{ padding: "0px", margin: "0px", marginBottom: "-5rem" }}>
            <Col
              md={{ order: 1 }}
              xl={12}
              className="homePage-featured-articles"
            >
              <NewsLetter isRetireePortal={true} />
            </Col>

            <Col md={{ order: 2 }} xl={6} className="homePage-featured-recipes" style={{ marginTop: "1rem" }} >
              <FeaturedRecipes retireeId={selectedRetiree} />
            </Col>
            <Col md={{ order: 2 }} xl={6} className="homePage-featured-recipes" style={{ marginTop: "1rem" }}>
              <FeaturedArticules retireeId={selectedRetiree} />
            </Col>
          </Row>

          <Row style={{ padding: "0px", marginTop: "4rem" }}>
            <Col md={12} xl={12}>
              <br />


              <FlipBookModal
                show={showFlipBook}
                handleClose={() => setShowFlipBook(false)}
                pdfUrl="https://miiadevpublic.blob.core.windows.net/public-assets/SmartShopper_healthy_you_healthy_wallet_flyer_01202022_high.pdf"
                width="100%"
                height="80vh"
                className="retiree-flipbook"
              />
              <br />
            </Col>
          </Row>
        </Col>
      </div>
    );
  };

  return (
    <>
      {isRetireeNotAvailable ? (
        <RetireeNotAvailable locationName={unavailableLocationName} />
      ) : !selectedRetiree ? (
        <RetireeSelector selectedRetiree={selectedRetiree} />
      ) : (
        <RetireeContent selectedRetiree={selectedRetiree} />
      )}
    </>
  );
}





export default Retiree;